import { useRef, useState, useEffect } from "react";
import * as THREE from "three";

// Detect if device can handle 3D
export const useCanRender3D = () => {
  const [can, setCan] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setCan(false); return; }
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      setCan(!!gl && !mobile);
    } catch { setCan(false); }
  }, []);
  return can;
};

// Raw Three.js hero scene — particles + wireframe octahedron
export const HeroScene = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, el.clientWidth / el.clientHeight, 0.1, 100);
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setSize(el.clientWidth, el.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    // Gold particles
    const particleCount = 40;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 8;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 5;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particles = new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xC9A84C, size: 0.04, transparent: true, opacity: 0.5, sizeAttenuation: true }));
    scene.add(particles);

    // Wireframe octahedron
    const octa = new THREE.Mesh(
      new THREE.OctahedronGeometry(1.5, 0),
      new THREE.MeshBasicMaterial({ color: 0xC9A84C, wireframe: true, transparent: true, opacity: 0.15 })
    );
    scene.add(octa);

    let mouse = { x: 0, y: 0 };
    const onMouse = (e) => { mouse.x = (e.clientX / window.innerWidth - 0.5) * 2; mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMouse, { passive: true });

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const t = performance.now() * 0.001;
      // Drift particles
      const arr = geo.attributes.position.array;
      for (let i = 0; i < particleCount; i++) arr[i * 3 + 1] += Math.sin(t * 0.12 + i * 0.4) * 0.001;
      geo.attributes.position.needsUpdate = true;
      // Rotate octahedron
      octa.rotation.y = t * 0.12 + mouse.x * 0.3;
      octa.rotation.x = Math.sin(t * 0.08) * 0.15 + mouse.y * 0.2;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      camera.aspect = el.clientWidth / el.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(el.clientWidth, el.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="absolute inset-0 z-0" style={{ pointerEvents: 'none' }} />;
};

// Raw Three.js pyramid scene with break-apart animation
export const PyramidScene = ({ progress }) => {
  const containerRef = useRef(null);
  const progressRef = useRef(0);
  progressRef.current = progress;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, el.clientWidth / el.clientHeight, 0.1, 50);
    camera.position.z = 4.5;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setSize(el.clientWidth, el.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);
    const goldLight = new THREE.PointLight(0xC9A84C, 0.7, 20);
    goldLight.position.set(4, 4, 4);
    scene.add(goldLight);
    const purpleLight = new THREE.PointLight(0x8A2BE2, 0.3, 20);
    purpleLight.position.set(-3, -2, 3);
    scene.add(purpleLight);

    // Build 4 pyramid layers
    const group = new THREE.Group();
    scene.add(group);

    const colors = [0xC9A84C, 0x8A2BE2, 0xC9A84C, 0x8A2BE2];
    const total = 4;
    const layerH = 0.55;
    const layers = [];
    const dirs = [[-1.2, 0.4], [1.3, -0.3], [-1.0, -0.7], [1.1, 0.5]];

    for (let i = 0; i < total; i++) {
      const topR = 0.3 + ((total - i - 1) / total) * 0.9;
      const botR = 0.3 + ((total - i) / total) * 0.9;
      const geo = new THREE.CylinderGeometry(botR, topR, layerH, 4, 1);
      const mat = new THREE.MeshStandardMaterial({
        color: 0x1a1a1a, emissive: 0x000000, emissiveIntensity: 0,
        metalness: 0.7, roughness: 0.3, transparent: true, opacity: 0.85
      });
      const mesh = new THREE.Mesh(geo, mat);
      const baseY = -(total * layerH) / 2;
      mesh.position.y = baseY + i * layerH;
      group.add(mesh);
      layers.push({ mesh, mat, baseY: baseY + i * layerH, color: colors[i], dir: dirs[i] });
    }

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const p = progressRef.current;
      group.rotation.y = p * Math.PI * 0.4 + 0.4;

      layers.forEach((l, i) => {
        const layerStart = i / total;
        const layerProg = Math.max(0, Math.min(1, (p - layerStart) * total * 0.8));
        const isActive = layerProg > 0.15;
        const sep = layerProg * 2.0;

        l.mesh.position.set(
          l.dir[0] * sep * 0.35,
          l.baseY + sep * 0.2 * (i - 1.5),
          l.dir[1] * sep * 0.25
        );
        l.mesh.rotation.y = sep * 0.15 * (i % 2 === 0 ? 1 : -1);

        l.mat.color.setHex(isActive ? l.color : 0x1a1a1a);
        l.mat.emissive.setHex(isActive ? l.color : 0x000000);
        l.mat.emissiveIntensity = isActive ? 0.35 : 0;
      });

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      camera.aspect = el.clientWidth / el.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(el.clientWidth, el.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      layers.forEach(l => { l.mesh.geometry.dispose(); l.mat.dispose(); });
      if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" />;
};
